import assign from 'object-assign';
import namespace from './namespace';
import {RegistrationSteps, RegistrationStepsMap, ShippingTierMap} from '@upi/constants';
import {googleAnalyticsLayer} from './data-layer-ga4';
import {facebookPixelLayer} from './data-layer-fbq';
import CartService from '@services/CartService';
import OrdersService from '@services/OrdersService';
import prop from 'ramda/src/prop';

const dataLayer = namespace( 'dataLayer' );
const dataLayerProto = {
    async getCartData () {
        try {

            const cart = await CartService.getCart();
            const cartItems = cart.lineItems?.map( line => ({
                ...line.product,
                quantity: line.quantity
            }) );

            return {
                cart,
                cartItems
            }
        }
        catch ( err ) {
            console.error( `[DataLayer]: Failed to fetch cart data; ${err.message}` );
        }
    },

    async getOrder ( orderNumber ) {
        try {
            const {
                subtotal,
                shippingTotal,
                tax,
                lineItems,
                promotions
            } = await OrdersService.getOrderById( {orderNumber} );

            return {
                orderNumber: orderNumber,
                shippingTotal,
                tax,
                subtotal,
                items: lineItems?.map( prop( 'product' ) ),
                promoCode: promotions?.map( prop( 'code' ) ).toString()
            }
        }
        catch ( err ) {
            console.error( `[DataLayer]: Failed to fetch cart data; ${err.message}` );
        }
    }
};

dataLayer.create = ( obj ) => assign( {}, dataLayerProto, obj );

export const forms = dataLayer.create( {
    formSubmit ( formDetail ) {
        googleAnalyticsLayer.formSubmit( formDetail );
    }
} );

export const cart = dataLayer.create( {
    sendCrossSellItems ( list = [] ) {
        googleAnalyticsLayer.crossSellItems( list );
        return this;
    },

    sendItemsAdded ( items = [] ) {
        googleAnalyticsLayer.itemsAdded( items );
        facebookPixelLayer.itemsAdded( items );
        return this;
    },

    sendItemsRemoved ( items = [] ) {
        googleAnalyticsLayer.itemsRemoved( items );
        return this;
    }
} );

export const checkout = dataLayer.create( {
    async beginCheckout () {
        const {cartItems} = await this.getCartData();
        googleAnalyticsLayer.beginCheckout( cartItems );
    },

    async setPaymentOption ( paymentMethodType ) {
        const {cartItems} = await this.getCartData();
        googleAnalyticsLayer.addPaymentInfo( {paymentMethodType, items: cartItems} );
        return this;
    },

    async setShippingOption ( shippingMethod ) {
        const {cartItems} = await this.getCartData();
        googleAnalyticsLayer.addShippingInfo( {
            shippingMethod: ShippingTierMap[shippingMethod] || 'Standard',
            items: cartItems
        } );
        return this;
    },

    async purchaseComplete ( orderNumber ) {
        const lastOrder = await this.getOrder( orderNumber );

        if ( lastOrder ) {
            googleAnalyticsLayer.purchaseComplete( lastOrder );
            facebookPixelLayer.purchaseComplete( lastOrder.items );
        }
    }
} );

export const product = dataLayer.create( {
    sendProductView ( sku ) {
        googleAnalyticsLayer.productView( sku );
        facebookPixelLayer.productView( sku );
        return this;
    },
    sendProductClick ( item ) {
        googleAnalyticsLayer.productClick( item );
        return this;
    }
} );

export const promotions = dataLayer.create( {
    sendPromoView ( promo ) {
        googleAnalyticsLayer.promoView( promo );
        return this;
    },
    sendPromoClick ( promo ) {
        googleAnalyticsLayer.promoClick( promo );
        return this;
    }
} );

export const user = dataLayer.create( {
    sendUserRegistrationProgress ( {step, viewCount} ) {
        let result = null;
        switch ( step ) {
            case RegistrationSteps.ACCOUNT_VERIFICATION_REQUIRED:
                result = 'pending';
                break;
            case RegistrationSteps.ACCOUNT_VERIFIED:
                result = 'verified';
                break;
            default:
                result = null;
        }
        googleAnalyticsLayer.registrationProgress( {
            viewCount,
            step: RegistrationStepsMap[step] || 'Unknown',
            result
        } );
        return this;
    },
    sendUserLogin () {
        googleAnalyticsLayer.userLogin();
        return this;
    }
} );

const pageView = dataLayer.create( {
    homepage () {
        facebookPixelLayer.viewHomepage();
    },
    cart () {
        // const {cartItems} = await this.getCartData();
        // googleAnalyticsLayer.viewCart( cartItems );
        // facebookPixelLayer.viewCart();

        this.getCartData().then( ( {cartItems} ) => {
            googleAnalyticsLayer.viewCart( cartItems );
            facebookPixelLayer.viewCart();
        } )
    }
} );

dataLayer.cart = cart;
dataLayer.checkout = checkout;
dataLayer.forms = forms;
dataLayer.product = product;
dataLayer.promotions = promotions;
dataLayer.user = user;
dataLayer.pageView = pageView;

export default dataLayer;
